import React, { useEffect, useState, useRef } from "react";
import { fireStore } from "../../firebaseConfig";
import { collection, getDocs, query, where } from "firebase/firestore";
import "react-datepicker/dist/react-datepicker.css";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

const AreaVisits = () => {
  const threeDaysAgo = new Date();
  threeDaysAgo.setDate(threeDaysAgo.getDate() - 1);

  const [startDate, setStartDate] = useState(threeDaysAgo.getTime()); // Store as milliseconds
  const [endDate, setEndDate] = useState(Date.now()); // Store as milliseconds

  const [AreaVisits, setAreaVisits] = useState([]);
  const [filteredVisits, setFilteredVisits] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTerritory, setSelectedTerritory] = useState("");

  // Fetch Visits...
  useEffect(() => {
    const fetchAreaVisits = async () => {
      try {
        const collectionRef = collection(fireStore, "AreaVisits");
        const q = query(
          collectionRef,
          where("id", ">=", startDate),
          where("id", "<=", endDate)
        );
        const querySnapshot = await getDocs(q);

        const areaVisitsList = querySnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .sort((a, b) => b.id - a.id); // Sort newest first

        setAreaVisits(areaVisitsList);
        setFilteredVisits(areaVisitsList);
      } catch (error) {
        console.error("Error fetching AreaVisits:", error);
      }
    };

    fetchAreaVisits();
  }, [startDate, endDate]); // Re-run when date range changes

  // Fetch Territoreis...
  useEffect(() => {
    const fetchTerritories = async () => {
      try {
        const collectionRef = collection(fireStore, "Territories");
        const querySnapshot = await getDocs(collectionRef);
        const territoriesList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort territories in ascending order based on a property, e.g., 'name'
        const sortedTerritories = territoriesList.sort((a, b) => {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        });

        setTerritories(sortedTerritories);
      } catch (error) {
        console.error("Error fetching territories:", error);
      }
    };

    fetchTerritories();
  }, []);

  // Export
  const exportToPDF = async () => {
    const doc = new jsPDF("p", "pt", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 0;

    for (let i = 0; i < filteredVisits.length; i++) {
      const AreaVisitElement = document.getElementById(
        `AreaVisit-${filteredVisits[i].id}`
      );

      if (AreaVisitElement) {
        // lower scale for smaller images
        const canvas = await html2canvas(AreaVisitElement, { scale: 1 });

        // compress to JPEG instead of PNG
        const imgData = canvas.toDataURL("image/jpeg", 0.6); // 60% quality

        // calculate size with slight margin
        const imgWidth = pageWidth * 0.95;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        // handle page breaks
        if (yPosition + imgHeight > pageHeight) {
          doc.addPage();
          yPosition = 0;
        }

        doc.addImage(imgData, "JPEG", 0, yPosition, imgWidth, imgHeight);
        yPosition += imgHeight;
      }
    }

    doc.save("AreaVisits.pdf");
  };

  // Filters
  useEffect(() => {
    const filtered = AreaVisits.filter(
      (visit) =>
        visit.visitedTerritory.toLowerCase() ===
        selectedTerritory.toLocaleLowerCase()
    );

    setFilteredVisits(filtered);
    if (selectedTerritory === "All") {
      setFilteredVisits(AreaVisits);
    }
  }, [selectedTerritory]);

  // Filter Logbooks...
  useEffect(() => {
    const filtered = AreaVisits.filter((visit) =>
      visit.user.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredVisits(filtered);
    if (searchQuery === "") {
      setFilteredVisits(AreaVisits);
    }
  }, [searchQuery]);

  return (
    <div className="p-4 bg-lightgray h-full overflow-y-scroll">
      <div className="bg-white px-4 py-2 rounded-lg shadow-lg max-w-5xl mx-auto mt-2 lg:sticky top-0 z-50">
        <div className="flex flex-wrap justify-between items-center py-2">
          <h1 className="text-lg font-semibold text-liquidblue">
            Area Visits ({filteredVisits.length})
          </h1>

          <button
            onClick={exportToPDF}
            className="border border-darkteal font-bold bg-transparent transition duration-300 ease-in-out hover:bg-liquidblue hover:text-white hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-liquidblue focus:ring-opacity-50 active:bg-darkteal active:text-white active:shadow-md text-darkteal rounded-lg px-3 py-1"
          >
            PDF
          </button>
        </div>
        <div className="flex flex-wrap justify-between items-center mb-2 space-y-2 md:space-y-0">
          <select
            id="territories"
            className="w-full md:w-auto border border-gray-300 rounded-lg px-5 py-2 focus:ring focus:ring-blue-300"
            onChange={(e) => setSelectedTerritory(e.target.value)}
            value={selectedTerritory}
          >
            <option value="All">Select Territory</option>
            {territories.map((territory) => (
              <option key={territory.id} value={territory.name}>
                {territory.name}
              </option>
            ))}
          </select>

          <div className="flex items-center">
            {/* From Date Picker */}
            <label
              htmlFor="from-date"
              className="mr-2 font-lato text-liquidblue font-semibold"
            >
              From Date
            </label>
            <input
              type="date"
              className="flex-grow border p-2 rounded focus:ring focus:ring-blue-300"
              value={new Date(startDate).toISOString().split("T")[0]} // Convert timestamp to YYYY-MM-DD format
              onChange={(e) => setStartDate(new Date(e.target.value).getTime())} // Convert selected date to timestamp
            />
          </div>
        </div>

        <div className="flex flex-wrap justify-between items-center mb-4 space-y-2 md:space-y-0">
          <input
            type="text"
            placeholder="Search"
            className="w-full md:w-auto border border-gray-300 rounded-lg px-5 py-2 focus:ring focus:ring-blue-300"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <div className="flex items-center">
            <label
              htmlFor="to-date"
              className="mr-2 font-lato text-liquidblue font-semibold"
            >
              To Date
            </label>
            <input
              type="date"
              className="flex-grow border p-2 rounded focus:ring focus:ring-blue-300"
              value={new Date(endDate).toISOString().split("T")[0]} // Convert timestamp to YYYY-MM-DD format
              onChange={(e) => setEndDate(new Date(e.target.value).getTime())} // Convert selected date to timestamp
            />
          </div>
        </div>
      </div>
      {filteredVisits.map((item) => (
        <div
          id={`AreaVisit-${item.id}`}
          key={item.id}
          className="bg-white p-6 rounded-lg shadow-lg max-w-5xl mx-auto mt-4"
        >
          {/*<h1 className="text-xl font-semibold py-4 text-liquidblue">{item.user?.territory?.name}</h1>*/}

          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="flex justify-around">
                  <th className="py-2 px-4 border-b border-gray-200 text-left">
                    {item.user?.name}
                  </th>
                  <th className="py-2 px-4 border-b border-gray-200 text-left">
                    {item.user?.phone}
                  </th>

                  <th className="py-2 px-4 text-left">
                    {new Date(Number(item.id)).toLocaleDateString("en-GB")}{" "}
                    {new Date(Number(item.id)).toLocaleTimeString("en-GB", {
                      hour12: true,
                    })}{" "}
                  </th>
                  <th className="py-2 px-4 border-b border-gray-200 text-left">
                    {item.visitedTerritory ? (
                      <h1 className="border border-darkteal font-bold bg-transparent transition duration-300 ease-in-out hover:bg-liquidblue hover:text-white hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-liquidblue focus:ring-opacity-50 active:bg-darkteal active:text-white active:shadow-md text-darkteal rounded-lg px-4 py-1">
                        {item.visitedTerritory}
                      </h1>
                    ) : null}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="flex flex-row w-full">
                  <td className="py-2 px-4 border-b border-gray-200 flex-1">
                    <div className="space-y-6">
                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Visit Plan</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.visitPlan}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">On Time Arrival</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.onTimeArrival}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Personal Appearance</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.personalAppearance}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Dress Code</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.dressCode}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Hair</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.hair}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Shoes</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.shoes}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Vehicle Cleanliness</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.vehicleCleanness}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Seatbelt</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.seatbelt}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">engine Noise</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.engineNoise}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">in Drive Mobile</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.inDriveMobile}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Safe Driving</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.safeDriving}
                            />
                          </label>
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Safe Parking</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.safeParking}
                            />
                          </label>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 px-4 border-b border-gray-200 flex-1">
                    <div className="space-y-6">
                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Updated Log Book</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.updatedLogBook}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Vehicle Condition</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.vehicleCondition}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Pr Book</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.prbook}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Promotion</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.wisePromotion}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">FSO Met</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.fsoMet}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">FSO PE</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.fsoPE}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Farmer Visited</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.farmerVisited}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">Dealer Visited</span>
                        <div class="dark:bg-white">
                          <label className="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.dealerVisited}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <span className=" font-lato">FM Attended</span>
                        <div class="dark:bg-white">
                          <label class="text-white">
                            <input
                              className="dark:border-white-400/20 w-5 h-5"
                              type="checkbox"
                              checked={item.fmAttended}
                            />
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center ">
                        <span className="font-lato font-bold">Summary</span>
                      </div>
                      <div className="flex items-center">
                        <p className="w-full break-words font-lato font-extrabold">
                          {item.summary}
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AreaVisits;
